import React, { useEffect, useState, useCallback } from "react";
import api, { API, apiError, inr, levelLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Percent, Users, Wallet, Copy, Sparkles, ShieldCheck, Pencil, Trash2, Share2, Network, MessageCircle, FileDown } from "lucide-react";
import { PendingMembers } from "@/components/Franchise";
import { MemberCandidates } from "@/components/MemberCandidates";
import { GuideBookButton } from "@/components/GuideBookButton";

const STATUS_COLORS = { available: "bg-emerald-500", busy: "bg-amber-500", break: "bg-blue-500", offline: "bg-gray-500" };

function genPassword() {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  return "BUH-" + Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

export default function Team() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [team, setTeam] = useState([]);
  const [comm, setComm] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: genPassword() });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [editAgent, setEditAgent] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", password: "" });
  const [deleteAgent, setDeleteAgent] = useState(null);
  const [referral, setReferral] = useState(null);
  const [cwForm, setCwForm] = useState({ name: "", phone: "" });
  const [cwAgentId, setCwAgentId] = useState("");
  const [cwSaving, setCwSaving] = useState(false);

  const load = useCallback(async () => {
    const [t, c, r] = await Promise.all([
      api.get("/team"), api.get("/commissions"), api.get("/my-referral").catch(() => ({ data: null })),
    ]);
    setTeam(t.data);
    setComm(c.data);
    setReferral(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const referralLink = referral ? `${window.location.origin}/portal?ref=${referral.referral_code}` : "";

  const addCoworker = async () => {
    if (!cwForm.name || !cwForm.phone) { toast.error("Name & mobile required"); return; }
    setCwSaving(true);
    try {
      const payload = { ...cwForm };
      if (isOwner && cwAgentId) payload.agent_id = cwAgentId;
      await api.post("/coworkers", payload);
      toast.success("Associate Partner added");
      setCwForm({ name: "", phone: "" }); setCwAgentId("");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setCwSaving(false); }
  };

  const addAgent = async () => {
    setSaving(true);
    try {
      await api.post("/team", { workspace_id: localStorage.getItem("workspace_id"), ...form });
      setCreated({ ...form });
      toast.success("Associate Partner login created");
      setForm({ name: "", email: "", password: genPassword() });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const commByAgent = (id) => comm?.agents.find((a) => a.agent_id === id);

  const openEdit = (m) => { setEditAgent(m); setEditForm({ name: m.name, phone: m.phone || "", password: "" }); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { name: editForm.name, phone: editForm.phone };
      if (editForm.password) payload.password = editForm.password;
      await api.put(`/team/${editAgent.id}`, payload);
      toast.success("Associate Partner updated");
      setEditAgent(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/team/${deleteAgent.id}`);
      toast.success("Associate Partner removed");
      setDeleteAgent(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Workspace" title="Team & Commissions"
        subtitle="Add unlimited associate partners, issue their login IDs and track commission per customer"
        action={isOwner && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setCreated(null); setForm({ name: "", email: "", password: genPassword() }); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-agent-btn" className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><UserPlus className="w-4 h-4" /> Add Associate Partner</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#05261D] border-gold/20">
              <DialogHeader>
                <DialogTitle className="font-serif text-gold flex items-center gap-2"><UserPlus className="w-5 h-5" /> Create Associate Partner Login</DialogTitle>
                <DialogDescription>The company generates a login ID & password for each associate partner. There is no limit on associate partners.</DialogDescription>
              </DialogHeader>
              {created ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-gold/40 bg-gold/10 p-4">
                    <p className="eyebrow text-gold mb-2">Share these credentials with {created.name}</p>
                    <CredRow label="Login email" value={created.email} />
                    <CredRow label="Password" value={created.password} />
                  </div>
                  <Button onClick={() => setOpen(false)} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">Done</Button>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    <div><Label className="text-xs text-muted-foreground">Full name</Label><Input data-testid="agent-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-card/60" /></div>
                    <div><Label className="text-xs text-muted-foreground">Login email</Label><Input data-testid="agent-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-card/60" placeholder="agent@bengalumrah.com" /></div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Password</Label>
                      <div className="flex gap-2 mt-1">
                        <Input data-testid="agent-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-card/60 font-mono" />
                        <Button type="button" variant="outline" className="border-gold/40 text-gold" onClick={() => setForm({ ...form, password: genPassword() })}><Sparkles className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button data-testid="save-agent-btn" onClick={addAgent} disabled={saving || !form.name || !form.email} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{saving ? "Creating…" : "Create Login"}</Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="p-4 sm:p-8 space-y-6">
        {isOwner && <PendingMembers />}
        {referral && (
          <Card className="p-4 bg-card/40 border-gold/30" data-testid="referral-card">
            <p className="eyebrow text-gold mb-2 flex items-center gap-2"><Share2 className="w-4 h-4" /> Your Marketing / Referral Link</p>
            <p className="text-xs text-muted-foreground mb-3">Share this link anywhere. Every customer who joins through it is credited to you — the harder you market, the more you earn.</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={referralLink} data-testid="referral-link-input" className="bg-card/60 font-mono text-xs" />
              <Button type="button" data-testid="referral-copy-btn" onClick={() => { navigator.clipboard.writeText(referralLink); toast.success("Referral link copied"); }} className="bg-gold text-[#0a2e22] hover:bg-gold/90 gap-1.5 text-xs flex-shrink-0"><Copy className="w-3.5 h-3.5" /> Copy</Button>
              <a data-testid="referral-wa-btn" href={`https://wa.me/?text=${encodeURIComponent(`Assalamu Alaikum! Join Bengal Umrah House Travels through my link: ${referralLink}`)}`} target="_blank" rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/40 hover:bg-[#25D366]/25 transition-colors text-xs font-medium" title="Share on WhatsApp"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
              <a data-testid="my-prospectus-btn" href={`${API}/prospectus/member.pdf?ref=${referral.referral_code}&origin=${encodeURIComponent(window.location.origin)}`} target="_blank" rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-gold/50 text-gold hover:bg-gold/10 transition-colors text-xs font-medium" title="Download my prospectus"><FileDown className="w-3.5 h-3.5" /> My Prospectus</a>
              <GuideBookButton testid="umrah-guide-btn" small href={`${API}/prospectus/guide.pdf?ref=${referral.referral_code}&origin=${encodeURIComponent(window.location.origin)}`} />
            </div>
          </Card>
        )}

        {/* Commission summary */}
        <div className="grid sm:grid-cols-3 gap-4">
          <SummaryCard icon={Users} label="Associate Partners" value={team.filter((m) => m.role !== "owner").length} />
          <SummaryCard icon={Wallet} label="Total package revenue" value={inr(comm?.total_revenue || 0)} />
          <SummaryCard icon={Percent} label="Associate Partner commission" value={inr(comm?.total_commission || 0)} highlight />
        </div>

        {/* Team list */}
        <div>
          <p className="eyebrow text-gold mb-3">Associate Partners & Commission Ledger</p>
          <div className="space-y-3">
            {team.filter((m) => m.role !== "owner").map((m) => {
              const cm = commByAgent(m.id);
              return (
                <Card key={m.id} data-testid={`agent-row-${m.id}`} className="p-4 bg-card/40 border-border/60">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="w-11 h-11 rounded-full bg-primary/50 border border-gold/30 flex items-center justify-center text-lg font-serif text-gold relative">
                      {m.name[0]}
                      <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#05261D] ${STATUS_COLORS[m.status] || "bg-gray-500"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{m.member_no && <span className="text-gold font-mono text-xs mr-1.5" data-testid={`team-member-no-${m.id}`}>#{m.member_no}</span>}{m.name}</p>
                      <p className="text-[10px] eyebrow text-gold/80" data-testid={`team-level-${m.id}`}>{levelLabel(m.level)}{m.parent_agent_name ? ` · under ${m.parent_agent_name}` : ""}</p>
                      <p className="text-xs text-muted-foreground font-mono">{m.phone || m.email}</p>
                    </div>
                    <div className="flex gap-5 text-center flex-wrap">
                      <div><p className="eyebrow text-muted-foreground">Customers</p><p className="text-lg font-serif text-white">{cm?.customers || 0}</p></div>
                      <div><p className="eyebrow text-muted-foreground">Revenue</p><p className="text-lg font-serif text-white">{inr(cm?.revenue || 0)}</p></div>
                      <div><p className="eyebrow text-gold">Commission</p><p className="text-lg font-serif text-gold" data-testid={`team-comm-${m.id}`}>{inr(cm?.commission || 0)}</p></div>
                      <div><p className="eyebrow text-emerald-300">Given</p><p className="text-lg font-serif text-emerald-300" data-testid={`team-paid-${m.id}`}>{inr(cm?.paid || 0)}</p></div>
                      <div><p className="eyebrow text-red-300">Due</p><p className="text-lg font-serif text-red-300" data-testid={`team-due-${m.id}`}>{inr(cm?.due || 0)}</p></div>
                    </div>
                    <Badge variant="outline" className="capitalize border-border/70">{m.status}</Badge>
                    {isOwner && (
                      <div className="flex gap-1.5">
                        <Button size="icon" variant="ghost" data-testid={`edit-agent-${m.id}`} onClick={() => openEdit(m)} className="h-8 w-8 text-gold hover:bg-gold/15"><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" data-testid={`delete-agent-${m.id}`} onClick={() => setDeleteAgent(m)} className="h-8 w-8 text-destructive hover:bg-destructive/15"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
            {team.filter((m) => m.role !== "owner").length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No associate partners yet. Add one to start assigning customers.</p>}
          </div>
        </div>

        {/* Quick add (name + mobile login) */}
        <div data-testid="coworkers-section">
          <p className="eyebrow text-gold mb-3 flex items-center gap-2"><Network className="w-4 h-4" /> Add Associate Partner (name + mobile login)</p>
          <Card className="p-4 bg-card/40 border-border/60 mb-3">
            <div className="grid sm:grid-cols-3 gap-2">
              <Input data-testid="cw-name-input" placeholder="Full name" value={cwForm.name} onChange={(e) => setCwForm({ ...cwForm, name: e.target.value })} className="bg-card/60" />
              <Input data-testid="cw-phone-input" placeholder="+9198XXXXXXXX" value={cwForm.phone} onChange={(e) => setCwForm({ ...cwForm, phone: e.target.value })} className="bg-card/60" />
              {isOwner && (
                <select data-testid="cw-under-select" value={cwAgentId} onChange={(e) => setCwAgentId(e.target.value)} className="h-9 rounded-md border border-input bg-card/60 px-2 text-sm">
                  <option value="" className="bg-[#05261D]">Directly under company (Level 01)</option>
                  {team.filter((m) => m.role !== "owner").map((m) => <option key={m.id} value={m.id} className="bg-[#05261D]">Under {m.name} → Level {String((m.level || 1) + 1).padStart(2, "0")}</option>)}
                </select>
              )}
            </div>
            <Button data-testid="cw-add-btn" onClick={addCoworker} disabled={cwSaving} className="w-full mt-3 bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-1.5"><UserPlus className="w-4 h-4" /> {cwSaving ? "Adding…" : isOwner ? "Add Associate Partner" : "Add Associate Partner under me"}</Button>
            <p className="text-[11px] text-muted-foreground mt-2">Everyone is an Associate Partner with the same features and commission. Anyone who joins through a partner's link is placed one level below them (Level 01 → Level 02 → …).</p>
          </Card>
        </div>

        {!isOwner && <MemberCandidates />}

        {/* Owner */}
        {team.filter((m) => m.role === "owner").map((m) => (
          <Card key={m.id} className="p-4 bg-card/40 border-gold/30 flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-gold" />
            <div className="flex-1"><p className="font-medium">{m.name} <span className="text-xs text-muted-foreground font-normal">· {m.email}</span></p><p className="text-xs text-muted-foreground">Company — configures IVR, associate partners & views analytics</p></div>
            <Badge variant="outline" className="border-gold/40 text-gold">Company</Badge>
          </Card>
        ))}
      </div>

      {/* Edit agent dialog */}
      <Dialog open={!!editAgent} onOpenChange={(o) => { if (!o) setEditAgent(null); }}>
        <DialogContent className="bg-[#05261D] border-gold/20" data-testid="edit-agent-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-gold flex items-center gap-2"><Pencil className="w-5 h-5" /> Edit Associate Partner</DialogTitle>
            <DialogDescription>Update the partner's name, mobile number, or reset their password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Full name</Label><Input data-testid="edit-agent-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 bg-card/60" /></div>
            <div><Label className="text-xs text-muted-foreground">Mobile number</Label><Input data-testid="edit-agent-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="mt-1 bg-card/60" placeholder="+9198XXXXXXXX" /></div>
            <div><Label className="text-xs text-muted-foreground">New password (leave blank to keep)</Label><Input data-testid="edit-agent-password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} className="mt-1 bg-card/60" placeholder="Min. 6 characters" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="save-edit-agent-btn" onClick={saveEdit} disabled={saving || !editForm.name} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{saving ? "Saving…" : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete agent dialog */}
      <Dialog open={!!deleteAgent} onOpenChange={(o) => { if (!o) setDeleteAgent(null); }}>
        <DialogContent className="bg-[#05261D] border-destructive/30" data-testid="delete-agent-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-destructive flex items-center gap-2"><Trash2 className="w-5 h-5" /> Remove Associate Partner</DialogTitle>
            <DialogDescription>
              Remove <strong className="text-white">{deleteAgent?.name}</strong>? Their customers will become unassigned (not deleted). This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteAgent(null)} className="text-muted-foreground">Cancel</Button>
            <Button data-testid="confirm-delete-agent-btn" onClick={confirmDelete} disabled={saving} className="bg-destructive text-white hover:bg-destructive/90 font-semibold">{saving ? "Removing…" : "Remove Member"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, highlight }) {
  return (
    <Card className={`p-4 bg-card/40 border-border/60 ${highlight ? "border-gold/40 gold-glow" : ""}`}>
      <div className="flex items-center gap-2 mb-2 text-gold"><Icon className="w-4 h-4" /><span className="eyebrow text-muted-foreground">{label}</span></div>
      <p className={`text-2xl font-serif font-semibold ${highlight ? "text-gold" : "text-white"}`}>{value}</p>
    </Card>
  );
}
function CredRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gold/10 last:border-0">
      <div><p className="text-[10px] text-muted-foreground uppercase">{label}</p><p className="font-mono text-sm">{value}</p></div>
      <Button size="icon" variant="ghost" className="h-7 w-7 text-gold" onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5" /></Button>
    </div>
  );
}
